/**
 * What was sent, and therefore what the confirmation may truthfully offer to do next.
 *
 * ── The endpoint answers on TWO paths, and they are not the same invitation ──────────────────
 * `POST /customer/invite` (`CustomerOnboardingProcessor.InviteAsync`) branches on who already holds
 * the address, and the branch is readable on the wire in one field — `hasOpenEngagement`:
 *
 * · **A free address** is provisioned: a roster entry opens `Invited`, the coaching relation is
 *   begun in the same transaction, and an offer is issued. `hasOpenEngagement` is `true`, `id` is
 *   the new client's, and the controller then opens the intake survey from this trainer's own
 *   template — it calls `EnsureIntakeAssignmentForCustomerAsync` only on this branch.
 * · **An address that already has an account** is merely ASKED (`OfferToExistingAccountAsync`).
 *   Nothing is provisioned: `MapOfferToExistingAccount` returns `hasOpenEngagement: false`, `id` is
 *   the person's EXISTING customer row, and no intake is opened. There is no coaching relation yet,
 *   so `/klienci/{id}` refuses until they accept.
 *
 * Both paths issue a real offer that expires in 14 days (`InvitationLifetime`), which is why the
 * confirmation's timeline is drawn on both.
 *
 * Pure: no React, no DOM, no query hook, no i18next.
 */

export interface SentInvitation {
  readonly firstName: string;
  readonly lastName: string;
  readonly email: string;
  /**
   * The customer row the response named, or `null` when it named none (`id` is optional on the
   * wire). On the existing-account path this is a row that exists and that this trainer still
   * cannot open — see `sentFaceCards`.
   */
  readonly clientId: number | null;
  /**
   * The invitation went to somebody who already has a MoveBuddy account, so nothing was
   * provisioned. Read from `hasOpenEngagement === false` rather than from a status string: the
   * status is `"invited"` on both paths and cannot tell them apart.
   */
  readonly existingAccount: boolean;
  readonly at: Date;
}

/** The four things the confirmation's right column can offer. */
export type SentCard = 'plan' | 'survey' | 'measurements' | 'soon';

/**
 * Which cards this invitation earns.
 *
 * Two of the four are claims about work the endpoint did, and on the existing-account path it did
 * neither — so they are not drawn rather than drawn and wrong:
 *
 * · **`survey`** says the intake "już poszła". No intake is opened when the person already has an
 *   account, so the card would be a statement of something that did not happen.
 * · **`measurements`** deep-links to that client's Pomiary tab. It is right on the provisioning
 *   path — `AuthorizeCustomerDetailAccessAsync` passes on a current coaching relation, which a
 *   fresh invite has, and R2 wants measurements recorded against an `Invited` client — and wrong
 *   on the other, where there is no relation yet and the screen refuses. A `null` `clientId` cuts
 *   it too: a card that links nowhere is worse than a card that is not drawn.
 *
 * The other two hold either way. The plan is the trainer's own work and needs no client; the
 * greyed card is a statement about this platform.
 */
export function sentFaceCards(sent: SentInvitation): readonly SentCard[] {
  if (sent.existingAccount) return ['plan', 'soon'];
  return sent.clientId === null
    ? ['plan', 'survey', 'soon']
    : ['plan', 'survey', 'measurements', 'soon'];
}
